import { useState } from 'react';
import { Button, Field, InfoLabel, Select, Text, tokens, type LabelProps } from '@fluentui/react-components';
import { EntitySyncOptionsDialog } from './EntitySyncOptionsDialog';
import { DEFAULT_RECONCILE_OVERLAP_SECONDS, IncrementalOverlapField } from './IncrementalOverlapField';
import { NormalizedRetentionField } from './NormalizedRetentionField';
import type { EntityDiscoveryItem, SelectedEntity } from '../../api/types';
import { confirmPollingDraft, canSelectIncremental, entitySelectionIssues, reconcilePollingSelections, pollingModeChoice, pollingTimestampColumns, selectPollingWatermark, pollingIdentityKeys,
  selectedPollingIdentity, selectPollingIdentity, selectPollingMode, selectablePollingTimestampColumns } from './entitySyncConfig';

interface Props {
  entity: EntityDiscoveryItem;
  selection: SelectedEntity;
  validationMessage?: string;
  disabled: boolean;
  refreshing: boolean;
  refreshError: string | null;
  onRefresh: () => void;
  onDone: (selection: SelectedEntity) => void;
}

export function PostgreSQLPollingOptions({ entity, selection: savedSelection, validationMessage, disabled, refreshing, refreshError, onRefresh, onDone }: Props) {
  const [configuring, setConfiguring] = useState(false);
  const [resetNotice, setResetNotice] = useState(false);
  const resetTransientState = () => {
    setConfiguring(false);
    setResetNotice(false);
  };
  const preview = entity.polling;
  if (!preview) return null;
  const configureLabel = validationMessage
    ? (canSelectIncremental(entity, savedSelection) ? 'Confirm required' : 'Configure required')
    : 'Configure';
  const timestampColumns = pollingTimestampColumns(preview);
  const selectableTimestamps = selectablePollingTimestampColumns(preview);
  const suggestedTimestamp = selectableTimestamps?.some(column => column.name === preview.watermark_column)
    ? preview.watermark_column : null;
  const keys = pollingIdentityKeys(preview);
  const savedIdentity = selectedPollingIdentity(entity, savedSelection);
  const metadataMissing = timestampColumns === null || keys === null
    || !Array.isArray(preview.eligible_columns) || !Array.isArray(preview.primary_key_columns);
  return (
    <EntitySyncOptionsDialog selection={savedSelection} title={`Configure sync · ${entity.label}`}
      configureLabel={configureLabel} disabled={disabled || refreshing}
      configureAriaLabel={`${configureLabel} for ${entity.label}`}
      configureDescription={validationMessage || `Configure sync for ${entity.label}`}
      configureStyle={{ color: validationMessage && !disabled ? tokens.colorPaletteRedForeground1 : undefined }}
      doneDisabled={selection => selection.sync_mode === 'incremental'
        && !canSelectIncremental(entity, selection)}
      confirmDraft={selection => confirmPollingDraft(entity, selection)}
      reconcileVersion={entity}
      reconcileDraft={(selection, previousEntity) => reconcilePollingSelections(
        new Map([[entity.name, selection]]), [previousEntity as EntityDiscoveryItem], [entity],
      ).get(entity.name)!}
      onOpenChange={resetTransientState} onDone={onDone}
      summary={<>
        {savedSelection.sync_mode === 'incremental'
          ? (savedSelection.incremental_behavior === 'append_only' ? 'Incremental append-only' : 'Incremental reconcile')
          : 'Full snapshot'}
        {savedSelection.sync_mode === 'incremental' && ` · ${savedSelection.incremental_field || 'None — select a column'}`}
        {savedSelection.sync_mode === 'incremental' && savedIdentity.length > 0
          && ` · Row identifier: ${savedIdentity.join(', ')}`}
        {metadataMissing && <Text size={200} role="status">Table details are unavailable. Refresh table details before syncing new and updated rows.</Text>}
      </>}>
      {(selection, onChange) => {
        const eligible = canSelectIncremental(entity, selection);
        const draftValidationMessage = entitySelectionIssues(
          new Map([[entity.name, selection]]), [entity],
        ).get(entity.name);
        const unavailableTimestamp = selection.incremental_field
          && !selectableTimestamps?.some(column => column.name === selection.incremental_field);
        const identity = selectedPollingIdentity(entity, selection);
        const identityValue = JSON.stringify(identity);
        const defaultOverlapSeconds = selection.incremental_behavior === 'append_only'
          ? 30 : DEFAULT_RECONCILE_OVERLAP_SECONDS;
        // Keep settings visible after edits reset consent; incomplete mode choices cannot be saved.
        const showIncrementalSettings = configuring || selection.sync_mode === 'incremental';
        return (
              <div className="polling-options">
                <details className="polling-disclosure">
                  <summary>How syncing works</summary>
                  <div className="polling-options">
                    <Text size={200}><strong>First sync:</strong> Copies all rows.</Text>
                    <Text size={200}><strong>Later syncs:</strong> Full snapshot replaces the full copy each time.
                      Both incremental modes use your timestamp column to find changes. Append-only deduplicates by an optional identifier, or by a deterministic row hash when no identifier is selected.</Text>
                    <Text size={200}><strong>Late changes:</strong> The look-back window rechecks recent timestamps.
                      Changes arriving outside that window require Full snapshot.</Text>
                    <Text size={200}><strong>Deleted rows:</strong> Incremental reconcile scans all row identifiers and removes saved rows
                      missing from the source. Incremental append-only skips that scan and is only for immutable events.</Text>
                    <Text size={200}><strong>Changed settings:</strong> Changing the row identifier or timestamp column requires
                      confirmation with Done. Saved setting changes, including the look-back window, start with a full reload.
                      If the source no longer supports your settings, sync reloads all rows instead.</Text>
                  </div>
                </details>
                {draftValidationMessage && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{draftValidationMessage}</Text>}
                {(metadataMissing || refreshing || refreshError) && (
                  <div>
                    <Text size={200} role="status">
                      {refreshing ? 'Refreshing table details…' : refreshError || 'Table details are unavailable. Refresh to load row identifiers and timestamp columns.'}
                    </Text>
                    <Button size="small" disabled={disabled || refreshing} onClick={onRefresh}>
                      Refresh table details
                    </Button>
                  </div>
                )}
                <Field label={{ children: (_: unknown, props: LabelProps) => (
                  <InfoLabel {...props} infoButton={{ 'aria-label': 'About sync modes' }}
                    info={<div className="polling-options">
                      <Text size={200}>Full snapshot replaces the saved data with a fresh copy on every sync.</Text>
                      <Text size={200}>Incremental reconcile polls new and updated rows and scans identifiers to infer source deletes.</Text>
                      <Text size={200}>Incremental append-only polls immutable events, optionally deduplicates by an identifier, and retains 180 days by default.</Text>
                      <Text size={200}>
                        Choosing an incremental mode, then Done and Save Selection on the page, confirms that its timestamp moves forward whenever a row is added or edited.
                        Reconcile also requires a unique identifier that never changes.
                        Timestamps without a time zone must use UTC. Reconcile checks all row identifiers to remove deleted rows;
                        append-only does not detect source deletions.
                        Backdated or late changes outside the look-back window still require Full snapshot.
                        If unsure, ask the database owner before confirming.
                      </Text>
                    </div>}>
                    Sync mode
                  </InfoLabel>
                ) }}>
                  <Select disabled={disabled || refreshing} value={pollingModeChoice(selection)}
                    onChange={(_, data) => {
                      setConfiguring(data.value !== 'full');
                      setResetNotice(false);
                      onChange(selectPollingMode(entity, selection, data.value));
                    }}>
                    <option value="full">Full snapshot</option>
                    <option value="incremental_reconcile">Incremental reconcile</option>
                    <option value="incremental_append_only">Incremental append-only</option>
                  </Select>
                </Field>
                {showIncrementalSettings && <>
                {selection.sync_mode === 'incremental' && !eligible && <Text size={200} role="status">
                  Setup incomplete. Choose {selection.incremental_behavior === 'append_only' ? 'a timestamp column' : 'a supported row identifier and timestamp column'}, then choose Done to confirm before saving.
                </Text>}
                {resetNotice && <Text size={200} role="status">
                  Row identifier or timestamp column changed. Choose Done to confirm the final settings.
                </Text>}
                {!eligible && !metadataMissing && <Text size={200} role="status">
                  {keys?.length === 0 && selection.incremental_behavior !== 'append_only' ? 'No supported unique row identifier. Use Full snapshot or Append-only.'
                    : 'Choose a supported row identifier and timestamp column to sync new and updated rows.'}
                </Text>}
                <Field label={{ children: (_: unknown, props: LabelProps) => (
                  <InfoLabel {...props} infoButton={{ 'aria-label': 'About unique row identifiers' }}
                    info={<div className="polling-options">
                      <Text size={200}>Identifies the same row across syncs so changes update it rather than create duplicates. Values must be unique, never empty, and never change. If several columns identify a row together, they must all be selected. Only supported identifiers declared by the database are listed.</Text>
                      <Text size={200}>Database primary key: {preview.primary_key_columns?.join(', ') || (Array.isArray(preview.primary_key_columns) ? 'None declared' : 'Table details unavailable')}.</Text>
                    </div>}>
                    {selection.incremental_behavior === 'append_only' ? 'Unique row identifier (optional)' : 'Unique row identifier'}
                  </InfoLabel>
                ) }}>
                  <Select disabled={disabled || refreshing || keys === null || keys.length === 0} value={identityValue}
                    onChange={(_, data) => {
                      if (selection.incremental_behavior === 'append_only' && data.value === '[]') {
                        if (identity.length === 0) return;
                        setConfiguring(true);
                        setResetNotice(true);
                        onChange(selectPollingIdentity(selection, []));
                        return;
                      }
                      const key = keys?.find(candidate => JSON.stringify(candidate.columns) === data.value);
                      if (key && data.value !== identityValue) {
                        setConfiguring(true);
                        setResetNotice(true);
                        onChange(selectPollingIdentity(selection, key.columns));
                      }
                    }}>
                    {selection.incremental_behavior === 'append_only' && <option value="[]">None — deduplicate identical rows</option>}
                    {!keys?.some(key => JSON.stringify(key.columns) === identityValue)
                      && !(selection.incremental_behavior === 'append_only' && identity.length === 0) && (
                      <option value={identityValue} disabled>{identity.length ? `Unavailable: ${identity.join(', ')}` : 'No supported identifier'}</option>
                    )}
                    {(keys ?? []).map(key => (
                      <option key={key.name} value={JSON.stringify(key.columns)}>
                        {key.type === 'p' ? 'Primary' : 'Unique'}: {key.columns.join(', ')}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={{ children: (_: unknown, props: LabelProps) => (
                  <InfoLabel {...props} infoButton={{ 'aria-label': 'About change timestamp columns' }}
                    info="Choose a date-and-time column that moves forward whenever a row is added or edited, not just when it is created. A strong recommendation is preselected for new tables only. Sample data cannot prove that the column always changes; check with the database owner.">
                    Change timestamp column
                  </InfoLabel>
                ) }} hint={suggestedTimestamp ? `Suggested column: ${suggestedTimestamp}. Select it only if it changes whenever a row is added or edited.` : 'No suggested column. Choose one that changes whenever a row is added or edited.'}>
                  <Select disabled={disabled || refreshing || timestampColumns === null} value={selection.incremental_field || ''}
                    onChange={(_, data) => {
                      if (data.value === (selection.incremental_field || '')) return;
                      setConfiguring(true);
                      setResetNotice(true);
                      onChange(selectPollingWatermark(selection, data.value));
                    }}>
                    <option value="">None — select a column</option>
                    {unavailableTimestamp && (
                      <option value={selection.incremental_field} disabled>Unavailable: {selection.incremental_field}</option>
                    )}
                    {(selectableTimestamps ?? []).map(column => (
                      <option key={column.name} value={column.name}>
                        {column.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                {unavailableTimestamp && <Text size={200} role="status">The selected timestamp is unavailable for incremental sync. Choose another column or Full snapshot.</Text>}
                {selectableTimestamps?.length === 0 && <Text size={200}>No eligible change timestamp columns are available. Use Full snapshot.</Text>}
                {selection.incremental_behavior === 'append_only' && <>
                  <Text size={200}>Append-only does not detect source deletions. With an identifier, overlap polls deduplicate matching events. Without one, deterministic row hashes deduplicate identical rows; byte-identical source events cannot be distinguished. Use it only when events are immutable.</Text>
                  <NormalizedRetentionField value={selection.retention_days}
                    policy={{ default_days: 180, minimum_days: 1, maximum_days: 36500 }}
                    disabled={disabled || refreshing}
                    onChange={days => onChange({ ...selection, retention_days: days,
                      source_contract_acknowledged: false })} />
                </>}
                <details className="polling-disclosure">
                  <summary>Advanced options</summary>
                  <div className="polling-options">
                    <IncrementalOverlapField value={selection.overlap_seconds}
                      defaultSeconds={defaultOverlapSeconds} disabled={disabled || refreshing}
                      hint={`Default: ${defaultOverlapSeconds} seconds${defaultOverlapSeconds === DEFAULT_RECONCILE_OVERLAP_SECONDS ? ' (5 minutes)' : ''}. Allowed: 0–86400 seconds (up to 1 day).`}
                      onChange={seconds => onChange({ ...selection, overlap_seconds: seconds })} />
                  </div>
                </details>
                </>}
              </div>
        );
      }}
    </EntitySyncOptionsDialog>
  );
}
